import {ChangeDetectorRef, Component} from '@angular/core';
import {forkJoin} from 'rxjs';
import {NgForm} from '@angular/forms';

import {Event, EventId} from '../../../models/base/event';

import {Query} from '../../../models/query/query';
import {CompanyService} from '../../../services/lookups/company.service';
import {StockroomService} from '../../../services/stockroom/stockroom.service';
import {BaseComponent} from '../../base/base.component';

import {Company} from '../../../models/lookups/company';
import {Stockroom} from '../../../models/stockroom/stockroom';
import {SelectItem} from 'primeng/primeng';
import {isNil} from 'lodash';

@Component({
  selector: 'bms-choose-company-stockroom',
  templateUrl: './choose-company-stockroom.html',
  styleUrls: ['./choose-company-stockroom.scss'],
  providers: [
    CompanyService,
    StockroomService
  ]
})
export class ChooseCompanyStockroomComponent extends BaseComponent {
  companyId: string;
  stockroomId: string;

  companies: Company[];
  /** All warehouses the user may access (from API). */
  stockrooms: Stockroom[];
  /** Active warehouses for the selected company. */
  stockroomsForCompany: Stockroom[] = [];

  companySelectItems: SelectItem[] = [];
  stockroomSelectItems: SelectItem[] = [];

  /** True when every allowed warehouse belongs to the same company. */
  singleCompanyMode = false;
  /** Read-only label when only one company applies. */
  companyDisplayName = '';

  constructor(
    private companyService: CompanyService,
    private stockroomService: StockroomService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  protected onShown() {
    super.onShown();
    this.companyId = null;
    this.stockroomId = null;
    this.stockroomsForCompany = [];
    this.companySelectItems = [];
    this.stockroomSelectItems = [];
    this.singleCompanyMode = false;
    this.companyDisplayName = '';

    const qryCmp: Query = new Query();
    qryCmp.addOrderByField(Company.TABLENAME, Company.COMPANY_NAME, Query.ASC);

    forkJoin(
      this.companyService.list(qryCmp),
      this.stockroomService.listAllowed()
    ).subscribe(data => {
      this.companies = data[0];
      this.stockrooms = data[1];
      this.setupAfterDataLoad();
    });
  }

  /** Treat missing isActive as active (legacy rows). */
  isStockroomActive(s: Stockroom): boolean {
    if (isNil(s)) {
      return false;
    }
    const raw = s as any;
    const v = !isNil(raw.isActive) ? raw.isActive : raw.is_active;
    if (v === undefined || v === null) {
      return true;
    }
    return v === true || v === 1;
  }

  private setupAfterDataLoad() {
    const companyIdsFromStockrooms = new Set(
      this.stockrooms
        .map(sr => sr.companyId)
        .filter(id => !isNil(id) && id !== '')
        .map(id => String(id))
    );
    this.singleCompanyMode = companyIdsFromStockrooms.size === 1;

    this.companySelectItems = (this.companies || []).map(c => ({
      label: c.companyName,
      value: String(c.id)
    }));

    if (this.singleCompanyMode) {
      const onlyId = String(Array.from(companyIdsFromStockrooms)[0]);
      this.companyId = onlyId;
      const comp = this.companies.find(c => String(c.id) === onlyId);
      this.companyDisplayName = comp ? comp.companyName : '';
    } else {
      const companyIdsOrdered = (this.companies || [])
        .map(c => String(c.id))
        .filter(id => companyIdsFromStockrooms.has(id));
      this.companyId =
        companyIdsOrdered.length > 0
          ? companyIdsOrdered[0]
          : (companyIdsFromStockrooms.size ? String(Array.from(companyIdsFromStockrooms)[0]) : null);
    }

    this.applyCompanyFilter();
  }

  /**
   * PrimeNG may emit onChange before ngModel writes companyId; prefer event.value when present.
   */
  onCompanyChange(event: any) {
    if (event != null && typeof event === 'object' && 'value' in event && event.value != null && event.value !== '') {
      this.companyId = String(event.value);
    }
    this.stockroomId = null;
    this.applyCompanyFilter();
  }

  private applyCompanyFilter() {
    const cid = this.companyId != null && this.companyId !== '' ? String(this.companyId) : '';
    if (!cid) {
      this.stockroomsForCompany = [];
    } else {
      this.stockroomsForCompany = this.stockrooms.filter(
        sr => String(sr.companyId) === cid && this.isStockroomActive(sr)
      );
    }
    this.rebuildStockroomItems();
    if (this.stockroomId != null && this.stockroomId !== '' &&
        !this.stockroomSelectItems.some(i => String(i.value) === String(this.stockroomId))) {
      this.stockroomId = null;
    }
    this.changeDetectorRef.detectChanges();
  }

  private rebuildStockroomItems() {
    this.stockroomSelectItems = this.stockroomsForCompany
      .map(sr => {
        const base = sr.code ? `${sr.name} (${sr.code})` : sr.name;
        return {
          label: base,
          value: String(sr.id)
        } as SelectItem;
      })
      .sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, {sensitivity: 'base'}));
  }

  onSelectCompanyAndStockroom(form: NgForm) {
    if (form.valid) {
      const cid = form.value.companyId != null ? String(form.value.companyId) : '';
      const sid = form.value.stockroomId != null ? String(form.value.stockroomId) : '';
      const stockr = this.stockrooms.find(str => String(str.id) === sid);
      let comp = this.companies.find(cmp => String(cmp.id) === cid);
      if (!comp && stockr) {
        comp = this.companies.find(cmp => String(cmp.id) === String(stockr.companyId));
      }
      this.fireEvent(
        new Event(EventId.DIALOG_OK, {company: comp, stockroom: stockr})
      );
      if (!this.router.url.startsWith('/ng/home')) {
        this.router.navigate(['ng/home']);
      }
    }
  }

  onClose() {
    this.fireEvent(new Event(EventId.DIALOG_CANCEL, null));
  }
}
